import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import {
  cn,
  IconCalendarAdd,
  IconCog,
  IconContact,
  IconHome,
  IconModule,
  IconPlus,
  IconRabbit,
  IconStar2,
  IconStore,
  IconTimesheet,
  IconTransferTruckTime,
  IconWarehouse1,
  TakiIcon,
  usePref,
} from '@taki/ui';
import type { IconProps } from '@taki/ui';
import type { ShellAppEntry } from './types.js';

/**
 * Dock d'apps vertical sur le bord gauche des surfaces de travail (croquis
 * fondateur, comportement valide en test visuel puis industrialise ici).
 *
 * Habillage PLAT : fond legerement grise (bg de la page), separe de l'app par
 * un simple filet. Grise au repos (opacity 50 %), plein au survol (300 ms),
 * regrisement 1,2 s apres la sortie de la souris, en 2 s.
 *
 * Le survol n'ouvre pas tout de suite : il faut RESTER sur le rail pendant la
 * latence d'intention ({@link DOCK_OPEN_DELAY_MS}) pour que le dock s'eveille
 * et se deploie. Une souris qui ne fait que traverser ne declenche plus rien.
 *
 * Contenu, de haut en bas : logo Taki (ouvre le menu home overlay), apps
 * mises en avant (pref "dock.apps"), shortcuts d'actions (pref
 * "dock.shortcuts"), roue Reglages (navigue vers /settings).
 */

/** Nombre maximal d'apps affichees dans le dock. */
export const DOCK_APPS_MAX = 6;

/** Nombre de shortcuts par defaut quand la pref n'est pas encore posee. */
const DEFAULT_SHORTCUTS_COUNT = 2;

/**
 * Map curatee nom -> composant des icones proposees en premier pour les
 * shortcuts du dock. Le `iconName` d'une action ou d'une pref accepte SOIT un
 * nom PascalCase de cette map, SOIT une clef kebab du registre complet de
 * @taki/ui (charge en dynamique, cf. loadIconRegistry / resolveDockIcon).
 */
export const DOCK_ICONS: Record<string, ComponentType<IconProps>> = {
  IconModule,
  IconCalendarAdd,
  IconPlus,
  IconContact,
  IconRabbit,
  IconTransferTruckTime,
  IconWarehouse1,
  IconStore,
  IconTimesheet,
  IconStar2,
  IconCog,
  IconHome,
};

/* ---- Registre complet des icones (clefs kebab), charge en dynamique ---- */

// Cache module du registre complet : une seule requete par session, partagee
// entre le dock et le hub Parametres.
let iconRegistryCache: Record<string, string> | null = null;
let iconRegistryPromise: Promise<Record<string, string>> | null = null;

/** Registre complet deja charge, sinon null (chargement : loadIconRegistry). */
export function iconRegistrySnapshot(): Record<string, string> | null {
  return iconRegistryCache;
}

/**
 * Charge le registre complet des icones (`@taki/ui/icon-registry`) en import()
 * DYNAMIQUE : le module est lourd (~centaines de KB), il ne part jamais dans
 * le bundle du shell. Il n'arrive que quand un shortcut porte une clef kebab
 * ou quand l'utilisateur ouvre le picker complet du hub Parametres.
 */
export function loadIconRegistry(): Promise<Record<string, string>> {
  iconRegistryPromise ??= import('@taki/ui/icon-registry').then((module) => {
    iconRegistryCache = module.iconRegistry;
    return module.iconRegistry;
  });
  return iconRegistryPromise;
}

/**
 * Icone du registre complet rendue depuis son inner SVG (meme convention que
 * le set @taki/ui : viewBox 24, fill currentColor, taille en rem pour suivre
 * l'echelle racine).
 */
export interface RegistryIconProps extends IconProps {
  /** Inner SVG (chaine) d'une clef kebab du registre complet. */
  inner: string;
}

export function RegistryIcon({ inner, size = 20, ...props }: RegistryIconProps): JSX.Element {
  return (
    <svg
      width={`${size / 16}rem`}
      height={`${size / 16}rem`}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}

// Wrappers memoises des icones du registre : un composant STABLE par clef,
// sinon chaque rendu remonterait le petit svg.
const registryIconComponents = new Map<string, ComponentType<IconProps>>();

/**
 * Resout l'`iconName` d'un shortcut : nom PascalCase de la map curatee
 * DOCK_ICONS, sinon clef kebab du registre complet (quand il est charge),
 * sinon module neutre (qui sert aussi de placeholder pendant le chargement).
 */
export function resolveDockIcon(
  name: string,
  registry?: Record<string, string> | null,
): ComponentType<IconProps> {
  const curated = DOCK_ICONS[name];
  if (curated != null) return curated;
  const inner = registry?.[name];
  if (inner == null) return IconModule;
  let component = registryIconComponents.get(name);
  if (component == null) {
    component = function DockRegistryIcon(props: IconProps) {
      return <RegistryIcon inner={inner} {...props} />;
    };
    registryIconComponents.set(name, component);
  }
  return component;
}

/** Forme stockee dans la pref "dock.shortcuts". */
export interface DockShortcutPref {
  appId: string;
  actionId: string;
  iconName: string;
}

/** Shortcut resolu contre les actions declarees (libelles inclus). */
export interface ResolvedDockShortcut {
  appId: string;
  appLabel: string;
  actionId: string;
  actionLabel: string;
  iconName: string;
}

/**
 * Resout la pref "dock.apps" en entrees d'apps : ids valides uniquement,
 * doublons ignores, plafond {@link DOCK_APPS_MAX}. Pref absente (null) =
 * defaut : les apps installees dans l'ordre, tronquees.
 */
export function resolveDockApps(
  pref: readonly string[] | null,
  entries: readonly ShellAppEntry[],
): ShellAppEntry[] {
  if (pref == null) return entries.slice(0, DOCK_APPS_MAX);
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const resolved: ShellAppEntry[] = [];
  for (const id of pref) {
    const entry = byId.get(id);
    if (entry == null || resolved.includes(entry)) continue;
    resolved.push(entry);
    if (resolved.length >= DOCK_APPS_MAX) break;
  }
  return resolved;
}

/**
 * Resout la pref "dock.shortcuts" contre les actions declarees des apps
 * (`entry.actions`) ; les entrees orphelines (app ou action disparue) sont
 * ignorees. Pref absente (null) = defaut : les premieres actions declarees
 * trouvees (ordre du registre), au plus {@link DEFAULT_SHORTCUTS_COUNT}.
 */
export function resolveDockShortcuts(
  pref: readonly DockShortcutPref[] | null,
  entries: readonly ShellAppEntry[],
): ResolvedDockShortcut[] {
  if (pref == null) {
    const found: ResolvedDockShortcut[] = [];
    for (const entry of entries) {
      for (const action of entry.actions ?? []) {
        found.push({
          appId: entry.id,
          appLabel: entry.label,
          actionId: action.id,
          actionLabel: action.label,
          iconName: action.iconName,
        });
        if (found.length >= DEFAULT_SHORTCUTS_COUNT) return found;
      }
    }
    return found;
  }
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const resolved: ResolvedDockShortcut[] = [];
  for (const item of pref) {
    const entry = byId.get(item.appId);
    const action = entry?.actions?.find((declared) => declared.id === item.actionId);
    if (entry == null || action == null) continue;
    resolved.push({
      appId: entry.id,
      appLabel: entry.label,
      actionId: action.id,
      actionLabel: action.label,
      iconName: item.iconName,
    });
  }
  return resolved;
}

/** Pastille du dock : meme DA que le menu home (active = teinte lime,
    survol = blanc), sans bordure au repos. La pastille est TOUJOURS en pleine
    largeur du rail (replie, ca fait 36 px = la colonne d'icone seule) : comme
    seule la largeur du rail s'anime, la pastille la suit image par image, sans
    jamais re-passer par un etat « centre » qui ferait sauter l'icone au repli.
    L'icone garde sa colonne de 36 px a gauche, deploye ou non. */
function cnDock(active: boolean, awake = false): string {
  return cn(
    'flex h-9 w-full items-center gap-2.5 rounded-full pr-2.5 transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
    // La chip active suit le MEME tempo que le fondu du dock (300 ms a
    // l'eveil, 2 s au regrissement), sinon elle semble regriser avant le reste.
    active
      ? awake
        ? 'border border-accent-border bg-accent-soft text-ink-900 duration-300'
        : 'border border-accent-tint-border bg-accent-tint text-accent-ink duration-[2000ms]'
      : 'text-ink-600 duration-150 hover:bg-surface hover:text-ink-900 hover:shadow-card',
  );
}

/**
 * Contenu d'une ligne du dock : la colonne d'icone (36 px, fixe) puis le
 * libelle, TOUJOURS monte (jamais demonte/remonte) : c'est le bord anime du
 * rail qui le revele ou l'avale (troncature), double d'un fondu d'opacite au
 * meme tempo (300 ms). Replie, il mesure 0 px et ne peint rien. L'icone reste
 * a la meme abscisse, deploye ou non.
 */
function DockRowInner({
  icon,
  label,
  expanded,
  badge = false,
}: {
  icon: JSX.Element;
  label: string;
  expanded: boolean;
  badge?: boolean;
}): JSX.Element {
  return (
    <>
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        {icon}
        {badge && <DockPlusBadge />}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-left text-[0.8125rem] font-medium',
          'transition-opacity duration-300 ease-in-out',
          expanded ? 'opacity-100' : 'opacity-0',
        )}
      >
        {label}
      </span>
    </>
  );
}

/** Petit filet horizontal separant les groupes du dock. */
function DockRule(): JSX.Element {
  return <span aria-hidden className="my-1 h-px w-6 shrink-0 self-center bg-border" />;
}

/** Badge lime « + » des shortcuts de creation du dock. */
function DockPlusBadge(): JSX.Element {
  return (
    <span
      className={cn(
        'absolute -right-[0.125rem] -top-[0.125rem] flex h-[0.875rem] w-[0.875rem] items-center justify-center',
        'rounded-full border-[1.5px] border-surface bg-accent text-accent-ink',
      )}
    >
      <IconPlus size={7} />
    </span>
  );
}

export interface DockProps {
  /** Apps installees, dans l'ordre de `config.installedApps`. */
  appEntries: readonly ShellAppEntry[];
  /** Id de l'app couramment montee (chip active), absent sur /settings. */
  activeAppId?: string;
  /** Selection d'une app : le shell navigue vers /app/<id>. */
  onPickApp: (appId: string) => void;
  /** Clic d'un shortcut : le shell pose la pending action puis navigue. */
  onPickShortcut: (appId: string, actionId: string) => void;
  /** Clic sur le logo Taki : ouvre le MENU HOME overlay. */
  onOpenHomeMenu: () => void;
  /** Clic sur la roue Reglages : le shell navigue vers /settings. */
  onOpenSettings: () => void;
}

/** Delai avant regrisement une fois la souris sortie (ms). */
const DOCK_SLEEP_DELAY_MS = 1200;

/**
 * Latence d'INTENTION avant ouverture au survol (ms) : le dock ne s'ouvre que
 * si la souris RESTE sur le rail, pas quand elle le traverse pour aller
 * ailleurs. Le compte a rebours est annule des que le pointeur ressort.
 *
 * 100 ms, cale au plus court apres test a l'oeil : l'ouverture reste percue
 * comme immediate quand on vise le dock, tout en coupant les traversees les
 * plus vives. C'est le plancher utile : une souris lente qui traverse le rail
 * replie met a peu pres ce temps a franchir ses 56 px, donc en dessous le
 * filtre ne filtre plus rien. Monter vers 150 ou 250 ms si un simple passage
 * ouvre encore par accident.
 */
const DOCK_OPEN_DELAY_MS = 100;

/** Vrai si le pointeur est encore dans le rectangle du rail. Sert a demasquer
    les sorties FANTOMES : quand la page bouge sous un curseur immobile (clic
    qui navigue, re-rendu), le navigateur peut emettre un mouseleave alors que
    la souris n'a pas bouge. La position reelle du pointeur, elle, ne ment
    pas : c'est LA source de verite du repli. */
function pointerInside(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
}

export function Dock({
  appEntries,
  activeAppId,
  onPickApp,
  onPickShortcut,
  onOpenHomeMenu,
  onOpenSettings,
}: DockProps): JSX.Element {
  // Le dock est grise au repos, plein au survol, et reste plein 1,2 s apres
  // la sortie de la souris avant de regriser en 2 s.
  const [dockAwake, setDockAwake] = useState(false);
  // Deploiement : au survol (ou focus clavier), le rail s'elargit vers la
  // droite pour afficher le nom de chaque icone. Contrairement au regrisement,
  // il se replie DES la sortie de la souris (la surface de travail voisine
  // reprend aussitot la largeur liberee), tant que dockAwake est encore vrai :
  // la meme transition (300 ms) porte donc le repli, avant que le regrisement
  // 2 s ne prenne le relais une fois la largeur deja revenue.
  const [expanded, setExpanded] = useState(false);
  // Vrai entre l'entree de la souris et l'ouverture reelle, pendant que court
  // la latence d'intention. C'est un ETAT et pas une ref : le filet de
  // securite ci-dessous doit se rebrancher pendant cette fenetre.
  const [openPending, setOpenPending] = useState(false);
  const asideRef = useRef<HTMLElement | null>(null);
  const dockTimer = useRef<number | undefined>(undefined);
  const openTimer = useRef<number | undefined>(undefined);
  const dockHovered = useRef(false);
  const cancelDockSleep = (): void => {
    if (dockTimer.current != null) window.clearTimeout(dockTimer.current);
    dockTimer.current = undefined;
  };
  const wakeDock = (): void => {
    cancelDockSleep();
    setDockAwake(true);
  };
  const sleepDock = (): void => {
    cancelDockSleep();
    dockTimer.current = window.setTimeout(() => setDockAwake(false), DOCK_SLEEP_DELAY_MS);
  };
  /** Desarme une ouverture programmee mais pas encore survenue. */
  const cancelDockOpen = (): void => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpenPending(false);
  };
  /** Ouverture IMMEDIATE (eveil + deploiement), sans latence : reservee au
      focus clavier, qui ne "passe" jamais sur le dock par accident. */
  const openDockNow = (): void => {
    cancelDockOpen();
    wakeDock();
    setExpanded(true);
  };
  /** Ouverture au SURVOL : programmee, puis honoree seulement si la souris est
      restee sur le rail pendant toute la latence d'intention. */
  const openDockAfterDelay = (): void => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    setOpenPending(true);
    openTimer.current = window.setTimeout(openDockNow, DOCK_OPEN_DELAY_MS);
  };
  useEffect(
    () => () => {
      if (dockTimer.current != null) window.clearTimeout(dockTimer.current);
      if (openTimer.current != null) window.clearTimeout(openTimer.current);
    },
    [],
  );

  // Filet de securite du repli : tant que le dock est deploye OU qu'une
  // ouverture est armee, le premier mouvement de souris HORS du rail le replie
  // et desarme l'ouverture, meme si le navigateur a avale la vraie sortie
  // pendant un re-rendu. Un mousemove porte la position REELLE du pointeur :
  // contrairement aux enter/leave, il ne ment jamais.
  useEffect(() => {
    if (!expanded && !openPending) return;
    const onMove = (event: MouseEvent): void => {
      const rect = asideRef.current?.getBoundingClientRect();
      if (rect == null || pointerInside(rect, event.clientX, event.clientY)) return;
      dockHovered.current = false;
      cancelDockOpen();
      sleepDock();
      setExpanded(false);
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
    // sleepDock / cancelDockOpen ne lisent que des refs : pas de vraies
    // dependances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, openPending]);

  // Prefs locales du poste : les usePref se resynchronisent en direct quand
  // le hub Parametres ecrit (event "taki:pref-change").
  const [appsPref] = usePref<string[] | null>('dock.apps', null);
  const [shortcutsPref] = usePref<DockShortcutPref[] | null>('dock.shortcuts', null);

  const dockApps = useMemo(() => resolveDockApps(appsPref, appEntries), [appsPref, appEntries]);
  const shortcuts = useMemo(
    () => resolveDockShortcuts(shortcutsPref, appEntries),
    [shortcutsPref, appEntries],
  );

  // Registre complet charge en dynamique au PREMIER shortcut kebab rencontre
  // (clef hors DOCK_ICONS) ; IconModule sert de placeholder le temps du
  // chargement, resolveDockIcon bascule des que le registre arrive.
  const [iconRegistry, setIconRegistry] = useState(iconRegistrySnapshot);
  const needsIconRegistry = shortcuts.some((shortcut) => DOCK_ICONS[shortcut.iconName] == null);
  useEffect(() => {
    if (!needsIconRegistry || iconRegistry != null) return;
    let cancelled = false;
    void loadIconRegistry().then((registry) => {
      if (!cancelled) setIconRegistry(registry);
    });
    return () => {
      cancelled = true;
    };
  }, [needsIconRegistry, iconRegistry]);

  // Aucune pref posee ET aucune app ne declare encore d'action : on garde les
  // 2 chips visuelles du test fondateur (New item / New reservation) comme
  // repli STATIQUE, purement decoratif. Le cablage reel des actions arrive en
  // phase suivante.
  const showStaticShortcuts = shortcutsPref == null && shortcuts.length === 0;
  const hasShortcutsBlock = shortcuts.length > 0 || showStaticShortcuts;

  return (
    <aside
      ref={asideRef}
      aria-label="Apps installees"
      onMouseEnter={() => {
        // Rien ne s'ouvre encore : on ARME l'ouverture, elle n'aura lieu que
        // si la souris est toujours la au bout de la latence d'intention.
        dockHovered.current = true;
        openDockAfterDelay();
      }}
      onMouseLeave={(event) => {
        // Sortie FANTOME : coordonnees encore dans le rail = la souris n'a
        // pas bouge, c'est la page qui a change dessous (clic sur une app,
        // navigation). On ignore : un clic ne referme JAMAIS le dock, seule
        // une vraie sortie de la souris le fait. Une ouverture armee survit
        // donc au clic, mais pas a une vraie sortie.
        const rect = event.currentTarget.getBoundingClientRect();
        if (pointerInside(rect, event.clientX, event.clientY)) return;
        dockHovered.current = false;
        cancelDockOpen();
        sleepDock();
        setExpanded(false);
      }}
      onFocusCapture={() => {
        openDockNow();
      }}
      onBlurCapture={(event) => {
        // Ne regrise / ne replie pas tant que la souris est sur le dock, ni
        // quand le focus reste a l'interieur (clic dans le vide).
        if (dockHovered.current) return;
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        cancelDockOpen();
        sleepDock();
        setExpanded(false);
      }}
      className={cn(
        // Fond legerement grise (bg de la page) + filet doux. La largeur
        // s'anime avec l'opacite (meme tempo) : replie 3,5 rem (icones seules),
        // deploye 13 rem (icone + nom). SEULE la largeur change entre les deux
        // etats : les lignes restent etirees en permanence (items-stretch,
        // jamais re-centrees) et suivent le rail image par image : c'est ce
        // qui garantit un repli aussi fluide que l'ouverture, sans saut
        // d'icone. shrink-0 + px constant : la colonne d'icone garde son
        // abscisse, la surface de travail voisine (flex-1) reprend/rend la
        // largeur au fil de la transition. Pas de border-r : le filet est
        // dessine PAR-DESSUS (span absolu), sinon la bordure mange 1 px de la
        // colonne interieure et l'icone n'est plus pile au centre de son
        // cercle quand le dock est replie (56 - 20 de padding - 1 = 35 px au
        // lieu de 36).
        'relative flex shrink-0 flex-col items-stretch gap-1.5 bg-bg px-2.5 pb-3 pt-4',
        'transition-[width,opacity] ease-in-out',
        expanded ? 'w-52' : 'w-[3.5rem]',
        dockAwake ? 'opacity-100 duration-300' : 'opacity-50 duration-[2000ms]',
      )}
    >
      {/* Filet de separation, dessine par-dessus le bord droit (cf. commentaire
          de la classe du rail : une bordure decalerait les icones d'1 px). */}
      <span aria-hidden className="absolute inset-y-0 right-0 w-px bg-border-soft" />

      {/* Marque Taki : ouvre le menu home (overlay du panneau Home). */}
      <button
        type="button"
        aria-label="Menu home"
        title="Home"
        onClick={onOpenHomeMenu}
        className={cn(
          'flex h-9 w-full items-center gap-2.5 rounded-full pr-2.5 text-ink-700',
          'transition-colors duration-300 ease-in-out hover:text-accent-soft-ink',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center">
          <TakiIcon width="1.25rem" height="1.25rem" />
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-left text-[0.8125rem] font-semibold',
            'transition-opacity duration-300 ease-in-out',
            expanded ? 'opacity-100' : 'opacity-0',
          )}
        >
          Home
        </span>
      </button>

      <DockRule />

      {/* Apps mises en avant (pref "dock.apps", defaut = installees, max 6). */}
      {dockApps.map((entry) => {
        const active = entry.id === activeAppId;
        return (
          <button
            key={entry.id}
            type="button"
            aria-label={entry.label}
            title={entry.label}
            aria-current={active ? 'true' : undefined}
            onClick={() => onPickApp(entry.id)}
            className={cnDock(active, dockAwake)}
          >
            {/* Icone du registre reduite pour la pastille du dock. */}
            <DockRowInner
              icon={
                isValidElement(entry.icon) ? (
                  cloneElement(entry.icon as JSX.Element, { size: 17 })
                ) : (
                  <>{entry.icon}</>
                )
              }
              label={entry.label}
              expanded={expanded}
            />
          </button>
        );
      })}

      {hasShortcutsBlock && <DockRule />}

      {/* Shortcuts d'actions (pref "dock.shortcuts") : le clic pose la
          pending action ({ appId, actionId }) puis le shell navigue ; l'app
          consomme l'action au montage et ouvre son Modal. */}
      {shortcuts.map((shortcut) => {
        const Icon = resolveDockIcon(shortcut.iconName, iconRegistry);
        const label = `${shortcut.appLabel} : ${shortcut.actionLabel}`;
        return (
          <button
            key={`${shortcut.appId}:${shortcut.actionId}`}
            type="button"
            aria-label={label}
            title={label}
            onClick={() => onPickShortcut(shortcut.appId, shortcut.actionId)}
            className={cnDock(false, dockAwake)}
          >
            <DockRowInner icon={<Icon size={17} />} label={label} expanded={expanded} badge />
          </button>
        );
      })}

      {/* Repli statique (visuel uniquement, cf. commentaire plus haut). */}
      {showStaticShortcuts && (
        <>
          <span aria-label="New item" title="New item" className={cnDock(false, dockAwake)}>
            <DockRowInner icon={<IconModule size={17} />} label="New item" expanded={expanded} badge />
          </span>
          <span
            aria-label="New reservation"
            title="New reservation"
            className={cnDock(false, dockAwake)}
          >
            <DockRowInner
              icon={<IconCalendarAdd size={17} />}
              label="New reservation"
              expanded={expanded}
              badge
            />
          </span>
        </>
      )}

      <div className="flex-1" />

      {/* Reglages : le hub Parametres du shell (/settings). */}
      <button
        type="button"
        aria-label="Reglages"
        title="Reglages"
        onClick={onOpenSettings}
        className={cnDock(false, dockAwake)}
      >
        <DockRowInner icon={<IconCog size={17} />} label="Reglages" expanded={expanded} />
      </button>
    </aside>
  );
}
